import type { MutableRefObject, ReactElement, ReactNode } from 'react';
import React, { useState, useEffect } from 'react';
import type { LocationSuggestOption } from '@construct-kit/core';
import { SearchMd } from '@construct-kit/core';
import type {
  ExtendedLocationSuggestOption,
  SelectedFiltersOptionsType,
} from '../model/constants/filterTypes';
import { dangerouslySetClassNames, StyledLocationSuggest } from './singleLocationSearchStyles';
import { fetchLocationSuggestions, locationTypeArray } from '../fetchLocationSuggestions';
import type { LocationSuggestionParams } from '../fetchLocationSuggestions';
import { customMapOptionToListNode } from '../locationSuggestUtils';

export const SINGLE_LOCATION_SUGGEST_ID = 'single-location-suggest';

type SingleLocationSearchProps = {
  selectedFiltersOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  singleLocationSuggestRef?: MutableRefObject<HTMLInputElement | null>;
  recentLocations?: ExtendedLocationSuggestOption[];
  icon?: ReactNode;
  handleOnClick?: () => void;
};

export const SingleLocationSuggest = ({
  selectedFiltersOptions,
  setSelectedFiltersOptions,
  singleLocationSuggestRef,
  recentLocations = [],
  icon = <SearchMd />,
  handleOnClick,
}: SingleLocationSearchProps): ReactElement => {
  const { searchLocations } = selectedFiltersOptions;
  const searchLocationsIsEmpty = !searchLocations || searchLocations.length === 0;
  const [searchLocation, setSearchLocation] = useState<string>();
  const [addressSuggestions, setAddressSuggestions] = useState<Array<LocationSuggestOption>>([]);
  const [showRecentSearches, setShowRecentSearches] = useState(true);

  useEffect(() => {
    if (!searchLocationsIsEmpty) {
      setSearchLocation(searchLocations[0].display.text);
    }
  }, [selectedFiltersOptions]);

  const onInputChange = async (inputValue: string) => {
    setSearchLocation(inputValue);

    const params: LocationSuggestionParams = {
      locationTypes: locationTypeArray,
      locationQuery: inputValue,
      maxResults: 7,
    };

    if (inputValue.length === 0) {
      setShowRecentSearches(true);
      setSelectedFiltersOptions({ ...selectedFiltersOptions, searchLocations: [] });
    } else {
      setShowRecentSearches(false);
      const fetchedSuggestions: Array<LocationSuggestOption> | undefined =
        (await fetchLocationSuggestions(params)) as Array<LocationSuggestOption> | undefined;

      if (fetchedSuggestions) {
        const updatedAddressSuggestions = [
          ...fetchedSuggestions,
          { display: { text: inputValue } },
        ];

        setAddressSuggestions(updatedAddressSuggestions);
      }
    }
  };

  const onSelectedOptionChange = (newSelectedLocationOptions: ExtendedLocationSuggestOption) => {
    setSelectedFiltersOptions({
      ...selectedFiltersOptions,
      searchLocations: [newSelectedLocationOptions],
    });
  };

  return (
    <StyledLocationSuggest
      ref={singleLocationSuggestRef}
      id={SINGLE_LOCATION_SUGGEST_ID}
      label={SINGLE_LOCATION_SUGGEST_ID}
      icon={icon}
      hideLabel
      openOnFocus
      disableListboxFlip
      // TODO: Re-enable when construct-kit team fixes issue with clear button
      // showClearButton
      inputValue={searchLocation}
      placeholder="Where are you looking?"
      onFocus={handleOnClick}
      options={showRecentSearches ? recentLocations : addressSuggestions}
      showRecentSearches={showRecentSearches}
      mapOptionToListNode={customMapOptionToListNode}
      onInputChange={onInputChange}
      onSelectedOptionChange={onSelectedOptionChange}
      dangerouslySetClassNames={dangerouslySetClassNames}
      mapOptionToString={(option) => option.display.text}
    />
  );
};

export default SingleLocationSuggest;
